import { cn } from '@ophelos/ui/lib/utils';
import { formatCurrencyParts, getLocaleFromLanguage, type PortalLocale } from '../../lib/currency';
import { useLanguage } from '@/contexts/LanguageContext';

interface CurrencyDisplayProps {
  amount: number;
  locale?: PortalLocale;
  currency?: string;
  className?: string;
  integerClassName?: string;
  centsClassName?: string;
  symbolClassName?: string;
  showSymbol?: boolean;
}

/**
 * Display currency with locale-aware thousand separators and styled cents
 *
 * @example
 * // English (UK): €1,234.56
 * <CurrencyDisplay amount={1234.56} locale="en-GB" />
 *
 * @example
 * // Portuguese (Portugal): €1.234,56
 * <CurrencyDisplay amount={1234.56} locale="pt-PT" />
 */
export function CurrencyDisplay({
  amount,
  locale,
  currency = 'EUR',
  className,
  integerClassName,
  centsClassName,
  symbolClassName,
  showSymbol = true,
}: CurrencyDisplayProps) {
  const { currentLanguage } = useLanguage();

  // Determine the locale to use
  // Priority: explicit locale prop > user's selected language > config default
  let resolvedLocale: PortalLocale;
  if (locale) {
    // If locale is provided, convert language code to locale if needed
    resolvedLocale = getLocaleFromLanguage(locale);
  } else {
    // Use current language from context (user's selection)
    resolvedLocale = getLocaleFromLanguage(currentLanguage);
  }

  const { integer, cents, symbol, decimalSeparator, symbolFirst } = formatCurrencyParts(amount, resolvedLocale, currency);
  const symbolSpan = showSymbol && <span className={cn(symbolClassName)}>{symbol}</span>;

  return (
    <span className={cn('inline-flex items-baseline gap-0.5', className)}>
      {symbolFirst && symbolSpan}
      <span className={cn('', integerClassName)}>{integer}</span>
      <span className={cn('', centsClassName)}>
        {decimalSeparator}
        {cents}
      </span>
      {!symbolFirst && symbolSpan}
    </span>
  );
}
